import { QrCode, Palette, Image, ScanLine } from "lucide-react";
import { GeometricBadge } from "../playful/GeometricBadge";
import { FloatingIcon } from "../playful/FloatingIcon";
import { Reveal } from "./Reveal";
import { QRGlyph } from "./mocks";

const FEATURES = [
  {
    icon: Palette,
    title: "Custom colors",
    desc: "Match QR foreground & background to your brand.",
  },
  {
    icon: Image,
    title: "Logo embedding",
    desc: "Drop your logo in the center — error correction auto-bumps.",
  },
  {
    icon: ScanLine,
    title: "Scan tracking",
    desc: "QR scans count as clicks in your analytics, by campaign.",
  },
];

/** QRBrandSection — branded QR + scan tracking. */
export default function QRBrandSection() {
  return (
    <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10" data-testid="qr-section">
      <div className="flex flex-col items-center gap-10 lg:flex-row">
        {/* QR sample */}
        <div className="order-2 w-full min-w-0 lg:order-1 lg:w-[42%]">
          <Reveal className="flex justify-center">
            <div className="relative inline-block rounded-3xl border-2 border-ink bg-white p-8 shadow-sticker-candy">
              <div className="rounded-2xl border-2 border-ink bg-white p-5">
                <QRGlyph cells={7} size="h-6 w-6" />
              </div>
              {/* center logo chip */}
              <span className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl border-2 border-ink bg-candy text-white shadow-sticker-xs">
                <QrCode size={20} strokeWidth={2.5} />
              </span>
              <div className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.24em] text-muted-foreground">
                linklane.io/launch
              </div>
            </div>
          </Reveal>
        </div>

        {/* Copy */}
        <div className="order-1 w-full min-w-0 lg:order-2 lg:w-[58%]">
          <Reveal>
            <GeometricBadge tone="candy">QR &amp; BRAND</GeometricBadge>
            <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tight md:text-5xl">
              Print-ready QR,
              <span className="ai-gradient-text"> on brand.</span>
            </h2>
            <p className="mt-5 max-w-lg font-sans text-base leading-relaxed text-ink/70 md:text-lg">
              Every short link ships with a QR code. On Pro, make it yours — custom colors, your
              logo in the middle, and SVG export for print. Every scan flows straight into
              analytics.
            </p>
          </Reveal>
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 80}>
                <div className="flex h-full flex-col gap-3 rounded-2xl border-2 border-ink bg-white p-5 shadow-sticker-xs">
                  <FloatingIcon icon={f.icon} tone="candy" size={40} />
                  <div>
                    <h3 className="font-display text-base font-extrabold">{f.title}</h3>
                    <p className="mt-1 font-sans text-[13px] leading-relaxed text-muted-foreground">
                      {f.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
